import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileDown, AlertCircle } from 'lucide-react';
import { formatDateLong } from '@/utils/dateFormat';

/**
 * Header strip shown above relief draft tables (Smart & Manual).
 */
export const ReliefDraftHeader = ({ draft, mode }) => (
  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
    <div className="flex items-center justify-between">
      <div>
        <p className="font-semibold text-slate-900">Covering for: {draft.absent_teacher_name}</p>
        <p className="text-sm text-slate-500">{formatDateLong(draft.date)} &mdash; {draft.total_periods} periods</p>
      </div>
      {mode === 'manual' ? (
        <span className="px-2 py-1 bg-slate-200 text-slate-700 rounded-full text-xs font-medium">Manual mode</span>
      ) : (
        <div className="flex gap-2 text-xs">
          <span className="px-2 py-1 bg-emerald-100 text-emerald-800 rounded-full font-medium">{draft.assigned_count} assigned</span>
          {draft.unassigned_count > 0 && (
            <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full font-medium">{draft.unassigned_count} unassigned</span>
          )}
        </div>
      )}
    </div>
  </div>
);

const MaterialsCell = ({ p }) => {
  if (p.period_files?.length > 0) {
    return (
      <div className="flex items-center gap-1 text-xs text-blue-600">
        <FileDown className="h-3 w-3" />
        <span>{p.period_files.length} file{p.period_files.length > 1 ? 's' : ''}</span>
      </div>
    );
  }
  if (p.period_remarks) {
    return <p className="text-[10px] text-slate-500 truncate max-w-[120px]" title={p.period_remarks}>{p.period_remarks}</p>;
  }
  return <span className="text-[10px] text-slate-300">None</span>;
};

/**
 * Draft editor table used by both Smart Auto-Assign and Manual Assign flows.
 *
 * mode === 'smart': shows match_type / unassigned_reason hints.
 * mode === 'manual': shows busy-teacher indicators and override warnings.
 */
export const ReliefDraftTable = ({
  draft,
  teachers,
  mode,
  onTeacherChange,
  isTeacherBusy = () => false,
  testIdPrefix = 'draft',
}) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm border-collapse" data-testid={`${testIdPrefix}-table`}>
      <thead>
        <tr className="border-b-2 border-slate-300">
          <th className="p-2 text-left text-xs font-semibold text-slate-500">Period</th>
          <th className="p-2 text-left text-xs font-semibold text-slate-500">Time</th>
          <th className="p-2 text-left text-xs font-semibold text-slate-500">Class</th>
          <th className="p-2 text-left text-xs font-semibold text-slate-500">Subject</th>
          <th className="p-2 text-left text-xs font-semibold text-slate-500">
            {mode === 'manual' ? 'Assign Relief Teacher' : 'Relief Teacher'}
          </th>
          <th className="p-2 text-left text-xs font-semibold text-slate-500 hidden md:table-cell">Comments</th>
          <th className="p-2 text-left text-xs font-semibold text-slate-500">Materials</th>
        </tr>
      </thead>
      <tbody>
        {draft.draft_periods.map((p, i) => {
          const busy = mode === 'manual' && p.relief_teacher_id && isTeacherBusy(p.period, p.relief_teacher_id);
          const triggerClass = mode === 'manual'
            ? `h-8 text-xs ${p.relief_teacher_id ? (busy ? 'border-red-300 bg-red-50' : p.match_type === 'nominated' ? 'border-blue-300 bg-blue-50' : 'border-blue-300 bg-blue-50') : 'border-slate-200'}`
            : `h-8 text-xs ${p.relief_teacher_id ? 'border-emerald-300 bg-emerald-50' : 'border-red-300 bg-red-50'}`;
          const sortedTeachers = mode === 'manual'
            ? [...teachers].map(t => ({ ...t, busy: isTeacherBusy(p.period, t.id) }))
                .sort((a, b) => a.busy - b.busy || a.name.localeCompare(b.name))
            : teachers;
          // Suggestion column text (shared across smart & manual when nominated/same_subject is present)
          const suggestionLabel = p.match_type === 'same_subject'
            ? 'Same subject match'
            : p.match_type === 'nominated'
              ? `Nominated by ${draft.absent_teacher_name}`
              : '';
          const suggestionClass = p.match_type === 'same_subject'
            ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
            : p.match_type === 'nominated'
              ? 'text-blue-700 bg-blue-50 border-blue-200'
              : 'text-slate-400';
          return (
            <tr key={i} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`${testIdPrefix}-row-${i}`}>
              <td className="p-2 font-medium text-slate-800">{p.period}</td>
              <td className="p-2 text-slate-500 text-xs">{p.time_slot}</td>
              <td className="p-2 font-medium text-slate-800">{p.class_name}</td>
              <td className="p-2 text-slate-700">{p.subject}</td>
              <td className="p-2">
                <Select value={p.relief_teacher_id || ''} onValueChange={(v) => onTeacherChange(i, v)}>
                  <SelectTrigger className={triggerClass} data-testid={`${testIdPrefix}-teacher-select-${i}`}>
                    <SelectValue placeholder={mode === 'manual' ? 'Select teacher...' : 'Unassigned'} />
                  </SelectTrigger>
                  <SelectContent>
                    {sortedTeachers.map(t => (
                      <SelectItem key={t.id} value={t.id} className={mode === 'manual' && t.busy ? 'text-red-500' : ''}>
                        {mode === 'manual' ? (t.busy ? '\u26A0 ' : '\u2713 ') : ''}{t.name}{mode === 'manual' && t.busy ? ' (Busy)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {/* Compact inline fallback shown on narrow screens (md:hidden) since Comments column is hidden there */}
                {suggestionLabel && p.relief_teacher_id && (
                  <span className={`md:hidden text-[10px] mt-0.5 block ${p.match_type === 'same_subject' ? 'text-emerald-600' : 'text-blue-600'}`} data-testid={`${testIdPrefix}-inline-suggestion-${i}`}>
                    {suggestionLabel}
                  </span>
                )}
                {/* Always-visible green note for subject experts (printed under teacher name regardless of screen size) */}
                {p.match_type === 'same_subject' && p.relief_teacher_id && (
                  <span className="text-[10px] text-emerald-600 mt-0.5 block font-medium hidden md:block" data-testid={`${testIdPrefix}-subject-note-${i}`}>
                    Same subject teacher
                  </span>
                )}
                {mode === 'smart' && !p.relief_teacher_id && p.unassigned_reason && (
                  <span className="text-[10px] text-red-500 mt-0.5 block">{p.unassigned_reason}</span>
                )}
                {mode === 'manual' && busy && (
                  <span className="text-[10px] text-red-500 mt-0.5 block flex items-center gap-0.5">
                    <AlertCircle className="h-3 w-3 inline" /> Unavailable — override applied
                  </span>
                )}
              </td>
              <td className="p-2 hidden md:table-cell" data-testid={`${testIdPrefix}-suggestion-${i}`}>
                {suggestionLabel && p.relief_teacher_id ? (
                  <span className={`inline-block text-[11px] font-medium px-2 py-0.5 rounded-full border ${suggestionClass}`}>
                    {suggestionLabel}
                  </span>
                ) : (
                  <span className="text-[10px] text-slate-300">—</span>
                )}
              </td>
              <td className="p-2"><MaterialsCell p={p} /></td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);
